/**
 * Align and distribute — pure geometry for arranging several rectangles at once.
 *
 * Everything is in surface coordinates (feet, origin bottom-left), so here
 * "left" is the low-x side and "top" is the high-y side. Callers that draw a
 * surface mirrored (the left / right walls) swap left and right before calling.
 *
 * These only say where things would GO. Whether the result is allowed is the
 * placement rules' call — apply the patches with `updateSections`, which checks
 * the whole surface at once and refuses the lot if anything would overlap or
 * leave the wall.
 */

import { Rect } from './resolver';

export interface ArrangeItem {
  id: string;
  rect: Rect;
}

/** Position changes keyed by item id — only the fields that change. */
export type RectPatches = Record<string, { x?: number; y?: number }>;

export type AlignMode = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';

/** The smallest rectangle containing all of them. */
export function boundsOf(rects: Rect[]): Rect {
  const x0 = Math.min(...rects.map((r) => r.x));
  const y0 = Math.min(...rects.map((r) => r.y));
  const x1 = Math.max(...rects.map((r) => r.x + r.width));
  const y1 = Math.max(...rects.map((r) => r.y + r.height));
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/**
 * Line every item up against one edge (or the centre line) of `frame` — the
 * whole surface, or the selection's own bounds.
 */
export function alignPatches(items: ArrangeItem[], mode: AlignMode, frame: Rect): RectPatches {
  const patches: RectPatches = {};
  for (const { id, rect } of items) {
    switch (mode) {
      case 'left': patches[id] = { x: frame.x }; break;
      case 'right': patches[id] = { x: frame.x + frame.width - rect.width }; break;
      case 'hcenter': patches[id] = { x: frame.x + (frame.width - rect.width) / 2 }; break;
      case 'bottom': patches[id] = { y: frame.y }; break;
      case 'top': patches[id] = { y: frame.y + frame.height - rect.height }; break;
      case 'vcenter': patches[id] = { y: frame.y + (frame.height - rect.height) / 2 }; break;
    }
  }
  return patches;
}

/**
 * How the spare room is shared out:
 *   - 'between' — equal gaps BETWEEN items; the first and last sit on the
 *     frame's edges. With the selection's bounds as the frame, that leaves the
 *     outermost items where they are.
 *   - 'around'  — equal gaps between items AND at both ends (so one item on its
 *     own is centred).
 */
export type DistributeSpacing = 'between' | 'around';

/**
 * Spread items along one axis of `frame` with equal gaps, keeping their order.
 *
 * The gap may come out NEGATIVE: items that sit in different rows can overlap
 * along the axis being distributed without touching each other (three wide
 * sections stacked up a wall, spread left to right, end up as a staircase).
 * Whether the result really collides is a two-dimensional question, and the
 * placement rules answer it when the patches are applied.
 *
 * Returns null when there is nothing to spread, or — for 'around' — when the
 * items are wider than the frame, since equal margins would then have to be
 * outside it.
 */
export function distributePatches(
  items: ArrangeItem[],
  axis: 'x' | 'y',
  frame: Rect,
  spacing: DistributeSpacing = 'between',
): RectPatches | null {
  const size = axis === 'x' ? 'width' : 'height';
  const n = items.length;
  if (n === 0) return null;
  if (spacing === 'between' && n < 2) return null;

  const sorted = [...items].sort((a, b) => (a.rect[axis] + a.rect[size] / 2) - (b.rect[axis] + b.rect[size] / 2));
  const spare = frame[size] - sorted.reduce((t, i) => t + i.rect[size], 0);
  if (spacing === 'around' && spare < -1e-6) return null;
  const gap = spare / (spacing === 'between' ? n - 1 : n + 1);

  const patches: RectPatches = {};
  let cursor = frame[axis] + (spacing === 'around' ? gap : 0);
  for (const item of sorted) {
    patches[item.id] = { [axis]: cursor };
    cursor += item.rect[size] + gap;
  }
  return patches;
}
